import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, X, CreditCard } from 'lucide-react';
import { posthog } from '../../providers/PostHogProvider';

interface GracePeriodBannerProps {
  gracePeriodEndsAt?: { seconds: number } | string | null;
}

/**
 * Dismissible warning banner shown when subscription is in GRACE_PERIOD state.
 * Displays days remaining and a link to update payment method.
 */
const GracePeriodBanner: React.FC<GracePeriodBannerProps> = ({ gracePeriodEndsAt }) => {
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    try { posthog?.capture('subscription_state_page_viewed', { state: 'grace_period' }); } catch {}
  }, []);

  if (dismissed) return null;

  // Calculate days remaining
  let daysRemaining = 7;
  if (gracePeriodEndsAt) {
    const endMs = typeof gracePeriodEndsAt === 'string'
      ? new Date(gracePeriodEndsAt).getTime()
      : (gracePeriodEndsAt as { seconds: number }).seconds * 1000;
    const now = Date.now();
    daysRemaining = Math.max(0, Math.ceil((endMs - now) / (1000 * 60 * 60 * 24)));
  }

  return (
    <div className="mx-4 mt-4 md:mx-6 md:mt-6 mb-0">
      <div className="relative flex items-center gap-3 px-4 py-3 rounded-lg bg-amber-900/30 border border-amber-700/50 text-amber-200">
        <AlertTriangle className="flex-shrink-0 text-amber-400" size={20} />
        <div className="flex-1 text-sm">
          <span className="font-medium">Payment issue detected.</span>{' '}
          {daysRemaining > 0
            ? `Your account has ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining before access is suspended.`
            : 'Your grace period has expired. Access may be suspended soon.'}
        </div>
        <button
          onClick={() => navigate('/settings/billing')}
          className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-amber-600 hover:bg-amber-500 text-white rounded-md transition-colors"
        >
          <CreditCard size={14} />
          Update Payment
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="flex-shrink-0 p-1 text-amber-400/60 hover:text-amber-300 transition-colors"
          aria-label="Dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export default GracePeriodBanner;
